// Angular imports
import { Component, OnInit, OnDestroy } from '@angular/core';

// Import services
import { MessageService, MessageType } from '../services';
import { ModalService } from '../modal/modal.service';

// Import modals
import { EditHourlyRateModalComponent } from '../modals/edit-hourly-wage-modal/edit-hourly-rate-modal.component';

// Import utils
import { DateFormatUtils } from '../utils';

// Import types
import { CalculationMetaApi, CalculationHeaderApi } from '../types';
import { BehaviorSubject, map, Subject, takeUntil } from 'rxjs';

// Import constants
import { selectCalculationMeta, selectHourRates } from '../state-management/calculation-selectors';
import { Store } from '@ngrx/store';
import { AppState } from '../state-management/app-state';
import * as CalculationActions from '../state-management/calculation-actions';
import * as HeaderActions from '../state-management/actions/header-line-actions'

@Component({
    selector: 'info-page',
    templateUrl: './info-page.component.html',
    styleUrls: ['./info-page.component.scss'],
    standalone: false
})
export class InfoPageComponent implements OnInit, OnDestroy {
    private unsubscribe$ = new Subject<void>();

    public meta$ = new BehaviorSubject<CalculationMetaApi>(undefined);

    /**
     * Net provisional costs are not allowed for variations calculation (meer-/minderwerk)
     * This observable is used to hide the checkbox
     * 
     *
     * @memberof InfoPageComponent
     */
    public _canHaveNetProvisionalPosts = this.meta$.pipe(
        takeUntil(this.unsubscribe$),
        map(meta => meta.templateName !== 'mmw')
    )

    public rates$ = new BehaviorSubject<CalculationHeaderApi[]>([]);
    public hourlyRates$ = this.rates$.asObservable();

    public streetName: string = '';
    public selectedRow: number = null;

    /**
     * The version date displayed on the exports is saved to a local variable
     * This is done so dates can be edited freely and the value is only validated on focus loss
     * All intermediate invalid dates will not lead to unwanted side effects
     *
     * @memberof InfoPageComponent
     */
    public $localDisplayDate = new BehaviorSubject<Date>(new Date());

    /**
     * While editing, only update this intermediate value
     * It can contain an invalid date string. Evaluation is done
     * on focus loss
     *
     * @memberof InfoPageComponent
     */
    public _intermediateDisplayDate: string;

    constructor(
        private messageService: MessageService,
        private modalService: ModalService,
        private store: Store<AppState>
    ) {
        // Update meta and local version date
        this.store.select(selectCalculationMeta).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(meta => {
            if (meta) {
                this.meta$.next({
                    ...meta,
                    address: meta.address ? { ...meta.address } : { address: '', city: '', country: '', postalCode: '' },
                    percentages: meta.percentages ? { ...meta.percentages } : {}
                });

                // Update the local display date
                this.$localDisplayDate.next(new Date(meta.displayDate || meta.versionDate || new Date().valueOf()))
            } else {
                this.meta$.next(undefined);

                // Update the local display date
                this.$localDisplayDate.next(null);
            }
        })

        this.store.select(selectHourRates).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(rates => {
            // console.log(rates);
            this.rates$.next(rates.map(r => ({ ...r })));
        })
    }

    public ngOnInit() {
    }

    public ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public get canDelete(): boolean {
        return this.selectedRow != null && this.rates$.value.length > 1 && this.rates$.value[this.selectedRow]?.id !== '00000000-0000-0000-0000-000000000000';
    }

    public clear() {
        this.selectedRow = null;
    }

    public addHourlyWage() {
        if (!this.meta$.value) {
            return;
        }

        const { id: calculationId, projectId } = this.meta$.value;

        if (!calculationId || !projectId) {
            return;
        }

        const highestOrder = this.rates$.value.reduce((p, c) => (c.sortOrder ?? 0) > p ? (c.sortOrder ?? 0) : p, 0);

        this.store.dispatch(HeaderActions.addHeaderLine({
            projectId,
            calculationId,
            rate: {
                description: `Uurloon ${this.rates$.value.length + 1}`,
                sortOrder: highestOrder + 100,
                value: 40,
                sourceVersion: this.meta$.value.currentVersion ?? 0,
                id: null
            }
        }));
    }

    public deleteSelected() {
        if (!this.canDelete) return;

        this.messageService.show({
            title: 'Waarschuwing',
            message: 'Door dit uurloon te verwijderen, zullen alle regels die dit uurloon gebruiken terug gezet worden naar het standaard uurloon. Weet je zeker dat je dit uurloon wil verwijderen?',
            type: MessageType.Warning,
            buttons: [
                {
                    text: 'Ja, verwijder',
                    handler: async () => {
                        // Get the selected hourly rate
                        const index = this.selectedRow;
                        const rate = this.rates$.value[index];

                        if (!this.meta$.value) {
                            return;
                        }
                
                        const { id: calculationId, projectId } = this.meta$.value;
                
                        if (!calculationId || !projectId) {
                            return;
                        }

                        this.store.dispatch(HeaderActions.deleteHeaderLine({
                            projectId,
                            calculationId,
                            rate: rate
                        }))

                        // Delete the hourly rate remote
                        // this.loaderService.performAsyncTask(async () => {
                        //     await this.dataService.deleteHourlyRate(this._project, this._calculation, hourlyWage);
                        // });

                        // this._calculation.hourlyRates.splice(index, 1);

                        // // Set all lines that used the hourly wage to default hourly wage
                        // this._calculation.categories.forEach(cat => {
                        //     cat.subcategories.forEach(subCat => {
                        //         subCat.lines.forEach(line => {
                        //             if (!(line instanceof CalculationLine)) return;
                        //             // if (line.hasSubLines || line.fixedNorms) return;
                        //             if (line.hours.hourlyRate !== hourlyWage) return;

                        //             // Assign default
                        //             line.hours.hourlyRate = this._calculation.hourlyRates[0];
                        //             this.dataService.updateLine(this._project, this._calculation, line);
                        //         });
                        //     });
                        // });

                        this.selectedRow = null;

                        // Recalculate
                        // this.applicationDataService.triggerRecalculation();
                    }
                },
                {
                    text: 'Terug'
                }
            ]
        });
    }

    public editHourlyRate(rate: CalculationHeaderApi) {
        this.modalService.open(EditHourlyRateModalComponent, { hourlyRate: rate, meta: this.meta$.value });
    }

    public onSelectRow(index: number) {
        this.selectedRow = index;
    }

    public onUpdateWage(wage: CalculationHeaderApi, quantity: number) {
        wage.value = quantity;
    }

    public onDateValueChange(value: string) {
        this._intermediateDisplayDate = value;
    }

    public onDateFieldFocusOut() {
        if (!this.meta$.value) {
            return;
        }
        // parse the date
        const newDate = DateFormatUtils.parseDate(this._intermediateDisplayDate);
        const oldDate = new Date(this.meta$.value?.versionDate);

        if (!!newDate && newDate.valueOf() !== oldDate?.valueOf()) {
    
            const { id: calculationId, projectId } = this.meta$.value;
    
            if (!calculationId || !projectId) {
                return;
            }

            this.store.dispatch(CalculationActions.updateMeta({
                projectId,
                calculationId,
                meta: {
                    ...this.meta$.value,
                    displayDate: newDate.toISOString()
                }
            }))
        } else {
            // Reset to currently saved value
            this.$localDisplayDate.next(new Date(this.meta$.value.versionDate));

            // this.$displayDate.next(new Date());

            // I hate this hack, but for now it is the fastest way to fix the Date field issues
            // setTimeout(() => {
            //     this.$displayDate.next(this._calculation.displayDate);
            // }, 0)

            // Also clear intemediate date value
            this._intermediateDisplayDate = '';
        }
    }

    public handleChangeMeta(callback?: () => void) {
        if (!this.meta$.value) {
            return;
        }

        const { id: calculationId, projectId } = this.meta$.value;

        if (!calculationId || !projectId) {
            return;
        }

        this.store.dispatch(CalculationActions.updateMeta({ projectId, calculationId, meta: this.meta$.value }));

        // // Clear existing timeout
        // if (this.metaUpdateTimeout)
        //     clearTimeout(this.metaUpdateTimeout);

        // // Set new timeout
        // this.metaUpdateTimeout = setTimeout(async () => {
        //     // Update calculation meta
        //     this.dataService.updateCalculationMeta(this._project, this._calculation).then(() => { if (callback) { callback() } });
        // }, META_UPDATE_TIMEOUT_DURATION) as any;
    }

    public async handleChangeNetPosts() {
        // Meta object holds the project and calculation id
        // Check if these values are available
        if (!this.meta$.value) {
            return;
        }

        const { id: calculationId, projectId } = this.meta$.value;

        if (!calculationId || !projectId) {
            return;
        }

        // Rename var for readability
        const currentValue = !!this.meta$.value.includeProvisionalPostLinesAfterTail;

        // If value is set to false, all 'sptN' units are replaced by 'spt'
        // Since net provisional posts are no longer allowed
        // Check with the user
        if (!currentValue) {

            // Only show message if there are net provisional posts
            // Ask permission to convert net posts
            if (!this.messageService.show({
                message: 'Deze actie zal alle netto stelposten in de calculatie omzetten naar bruto stelposten. Dit is onomkeerbaar. Weet je het zeker?',
                title: 'Waarschuwing',
                buttons: [
                    {
                        text: 'Ja',
                        handler: async () => {
                            this.store.dispatch(CalculationActions.updateMetaProvisionalSetting({
                                projectId,
                                calculationId,
                                meta: {
                                    ...this.meta$.value,
                                    includeProvisionalPostLinesAfterTail: false
                                }
                            }))

                            // Leaving this logic for now, until it is moved to the effects
                            // const updateLineCalls = [];

                            // for (const line of this._calculation.lines?.filter(l => l instanceof CalculationLine && l.unit === 'sptN')) {
                            //     (line as CalculationLine).unit = 'spt';
                            //     updateLineCalls.push(this.dataService.updateLine(this._project, this._calculation, line));
                            // }

                            // // Await these calls to prevend abortion when window is reloaded below
                            // this.loaderService.performAsyncTask(async () => { await Promise.all(updateLineCalls) });

                            // // Recalc first
                            // this.applicationDataService.triggerRecalculation();

                            // // Update meta with callback
                            // // This is a quick & dirty workaround in case net posts are converted to gross
                            // // This will not automatically update the icons in the UI
                            // // So reload for now
                            // this.handleChangeMeta(() => { window.location.reload() });
                        }
                    },
                    {
                        text: 'Nee',
                        handler: () => {
                            this.meta$.next({
                                ...this.meta$.value,
                                includeProvisionalPostLinesAfterTail: true
                            })
                        }
                    }
                ]
            })) {
                // Message cannot be shown, abort
                this.meta$.next({
                    ...this.meta$.value,
                    includeProvisionalPostLinesAfterTail: !currentValue
                })
                return;
            }

        } else {
            // Only update the meta object
            this.store.dispatch(CalculationActions.updateMeta({
                projectId,
                calculationId,
                meta: {...this.meta$.value}
            }))
        }
    }

    public handleDefaultPercentagesChange() {
        // Recalculate
        // this.applicationDataService.triggerRecalculation();
    }
}
